import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../../context/GlobalContext';
import useApi from '../../../hooks/useApi';
import haxios from '../../../lib/haxios';
import clsx from 'clsx';
import moment from 'moment';
import ReportLayout from '../core/ReportLayout';
import ReportKPICards from '../core/ReportKPICards';
import DataTable from '../shared/DataTable';
import { 
  Users, 
  TrendingUp, 
  DollarSign,
  Target,
  BarChart3,
  PieChart,
  LineChart
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart as RechartsLineChart,
  Line
} from 'recharts';
import { formatCurrency, formatPercentage, formatDate } from '../shared/utils';
import { exportToPDF, exportToExcel, prepareEmailData } from '../shared/exportUtils';
import BranchDropdown from '../../../components/BranchDropdown';

const CustomerAnalysis = () => {
  const { uinfo } = useContext(GlobalContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    branch: 'all',
    period: 'month',
    minSales: 0
  });
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [activeTab, setActiveTab] = useState('spends');
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState(null);
  const lastGoodDataRef = useRef(null); // Preserve last good processed data

  // Get branches for dropdown
  const { data: branches, isLoading: branchesLoading } = useApi({ 
    url: '/salesassist/cplusassist/branches', 
    initial: [],
    enabled: true
  });

  // Get customer sales data - use monthly aggregated data instead of "today" (which has zeros)
  const { data: sixMonthSalesData, isLoading: sixMonthLoading } = useApi({ 
    url: '/visiondashboard/salesassist/getSixMonthSales/', 
    initial: { data: [], months: [] }
  });

  // Get customer sales data (ranked customers) - only fetch if we have Inits
  const { data: customerRankData, isLoading: customerRankLoading } = useApi({ 
    url: uinfo?.Inits ? `/visiondashboard/rankCustomers?Rep=${uinfo.Inits}` : null,
    initial: []
  });

  // Get sales margin by branch and account (today) - keep for account structure but don't rely on it for values
  const { data: salesMarginByAccount, isLoading: salesMarginLoading } = useApi({ 
    url: '/salesassist/cplusassist/salesMarginByBranchAndAccountToday', 
    initial: [],
    enabled: true
  });

  // Get branch sales data (same API as dashboard for branch chart)
  const { data: branchSalesData, isLoading: branchSalesLoading } = useApi({ 
    url: '/salesassist/cplusassist/salesMarginByBranchAndInitsToday', 
    initial: [],
    enabled: true
  });

  // Get today's daily sales data across all branches for accurate total sales
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const formattedToday = moment().format('YYYYMMDD');
  const { data: todayDailySalesData, isLoading: todayDailySalesLoading } = useApi({ 
    url: `/visiondashboard/salesassist/getDailySales/${formattedToday}/${formattedToday}/`, 
    initial: { daily: [] },
    enabled: true
  });

  // Get voucher data
  const [voucherData, setVoucherData] = useState([]);
  const [loadingVoucherData, setLoadingVoucherData] = useState(false);

  useEffect(() => {
    const fetchVoucherData = async () => {
      if (!uinfo?.Inits) return;
      setLoadingVoucherData(true);
      try {
        const response = await haxios.get(`/visiondashboard/voucher-progress?Rep=${uinfo.Inits}`);
        if (response.success) {
          const voucherArray = Array.isArray(response.data) ? response.data : (response.data?.filtered || response.data?.data || []);
          setVoucherData(voucherArray || []);
        } else {
          setVoucherData([]);
        }
      } catch (error) {
        console.error('CustomerAnalysis - Error fetching voucher data:', error);
        setVoucherData([]);
      } finally {
        setLoadingVoucherData(false);
      }
    };
    fetchVoucherData();
  }, [uinfo?.Inits]);

  // Read customer filter from URL query parameter on mount
  useEffect(() => {
    const customerParam = searchParams.get('customer');
    if (customerParam) {
      const decodedCustomer = decodeURIComponent(customerParam);
      setSelectedCustomerFilter(decodedCustomer);
    } else {
      setSelectedCustomerFilter(null);
    }
  }, [searchParams]);

  // Process data directly in useMemo - simple like InvoiceSummary
  const processedData = useMemo(() => {
    // Don't process if still loading - use last good data if available, otherwise return zeros
    if (sixMonthLoading || customerRankLoading || salesMarginLoading || branchesLoading || branchSalesLoading || todayDailySalesLoading || loadingVoucherData) {
      if (lastGoodDataRef.current) {
        console.log('CustomerAnalysis - Using preserved data during loading');
        return lastGoodDataRef.current; // Return preserved data during loading
      }
      console.log('CustomerAnalysis - Still loading, returning zeros');
      return {
        kpis: [
          { key: 'total-customers', label: 'Total Customers', value: 0, type: 'customers', format: 'number' },
          { key: 'active-customers', label: 'Active Customers', value: 0, type: 'advisors', format: 'number' },
          { key: 'total-sales', label: 'Total Sales', value: 0, type: 'sales', format: 'currency' },
          { key: 'avg-sales', label: 'Avg Sales/Customer', value: 0, type: 'sales', format: 'currency' },
          { key: 'total-margin', label: 'Total Margin', value: 0, type: 'margin', format: 'currency' },
          { key: 'avg-margin-percent', label: 'Avg Margin %', value: 0, type: 'margin', format: 'percentage' },
          { key: 'total-rebate', label: 'Total Rebate Value', value: 0, type: 'margin', format: 'currency' },
          { key: 'total-voucher', label: 'Total Voucher Sales', value: 0, type: 'sales', format: 'currency' }
        ],
        topCustomers: [],
        chartData: [],
        branchData: [],
        tableData: []
      };
    }
    
    console.log('CustomerAnalysis - Processing data:', {
      sixMonthDataLength: sixMonthSalesData?.data?.length || 0,
      customerRankDataLength: customerRankData?.length || 0,
      salesMarginDataLength: salesMarginByAccount?.length || 0,
      branchSalesDataLength: branchSalesData?.length || 0,
      todayDailySalesData: todayDailySalesData
    });

    // Extract data from API responses - useApi returns data directly, not wrapped
    const safeSixMonthData = sixMonthSalesData?.data || [];
    const safeCustomerRankData = Array.isArray(customerRankData) ? customerRankData : [];
    const safeSalesMarginData = Array.isArray(salesMarginByAccount) ? salesMarginByAccount : [];
    const safeVoucherData = Array.isArray(voucherData) ? voucherData : [];
    
    // Use sixMonthSalesData as primary source (has actual sales values)
    // Fallback to customerRankData, then salesMarginByAccount for account structure
    
    // Combine data from both sources
    const processedCustomers = [];

    // PRIMARY: Process sixMonthSalesData (has actual sales values, not zeros)
    if (safeSixMonthData.length > 0) {
      // Log first customer to see structure
      if (safeSixMonthData.length > 0) {
        console.log('CustomerAnalysis - Sample sixMonthSalesData customer:', safeSixMonthData[0]);
      }
      
      safeSixMonthData.forEach(customer => {
        const accountId = customer._id || customer.account || customer.Account || customer.customerCode;
        if (!accountId) return;

        // Find matching data from other sources
        const rankData = safeCustomerRankData.find(c => 
          c._id === accountId || c.account === accountId
        );
        const salesData = safeSalesMarginData.find(item => 
          item.account === accountId || item.Account === accountId
        );
        const voucherInfo = safeVoucherData.find(v => 
          v.Acct === accountId || v.account === accountId
        ) || null;

        // Use sixMonthSalesData values (has real sales), fallback to others
        // Try multiple field names - getSixMonthSales uses 'total' field
        // Also check if we need to sum months object
        let salesValue = 0;
        if (customer.total !== undefined && customer.total !== null) {
          salesValue = parseFloat(customer.total);
        } else if (customer.sales !== undefined && customer.sales !== null) {
          salesValue = parseFloat(customer.sales);
        } else if (customer.totalSales !== undefined && customer.totalSales !== null) {
          salesValue = parseFloat(customer.totalSales);
        } else if (customer.months && typeof customer.months === 'object') {
          // Sum up all months if total is not available
          salesValue = Object.values(customer.months).reduce((sum, val) => sum + parseFloat(val || 0), 0);
        } else if (salesData) {
          // Fallback to salesMarginByAccount data
          salesValue = parseFloat(salesData.totalSales || salesData.invoiceValueToday || salesData.invoiceValue || 0);
        }
        
        const invoiceCount = parseInt(customer.invoices || customer.invoiceCount || salesData?.invoicesToday || salesData?.invoices || 0);
        const marginValue = parseFloat(customer.margin || customer.totalMargin || salesData?.totalMargin || salesData?.marginToday || 0);
        const marginPercent = salesValue > 0 ? ((marginValue / salesValue) * 100) : 0;

        processedCustomers.push({
          account: accountId,
          name: customer.customerName || customer.name || customer.Name || rankData?.GarageName || 'Unknown',
          branch: customer.branch || salesData?.branch || rankData?.branch || 'Unknown',
          sales: salesValue,
          invoices: invoiceCount,
          margin: marginValue,
          marginPercent: marginPercent,
          lastOrderDate: customer.lastOrderDate || rankData?.lastOrderDate || null,
          status: customer.status || rankData?.status || 'Active',
          rebateAnalysis: rankData?.rebateAnalysis || null,
          currentRebateAmount: rankData?.rebateAnalysis?.currentRebateAmount || 0,
          rebateProgress: rankData?.rebateAnalysis?.nextTier ? 
            Math.min(100, Math.max(0, ((rankData.rebateAnalysis.netMonthSales || 0) / (rankData.rebateAnalysis.nextTier.target || 1) * 100))) : 
            (rankData?.rebateAnalysis?.currentTier ? 100 : 0),
          voucherSales: voucherInfo?.Sales || 0,
          voucherTarget: voucherInfo?.Target || 0,
          voucherProgress: voucherInfo?.Target ? ((voucherInfo.Sales || 0) / voucherInfo.Target * 100) : 0
        });
      });
    }

    // FALLBACK: Process salesMarginByAccount data for customers not in sixMonthSalesData
    // OR if sixMonthSalesData has no sales values
    const hasSalesInSixMonth = processedCustomers.some(c => c.sales > 0);
    if (safeSalesMarginData.length > 0 && (!hasSalesInSixMonth || processedCustomers.length === 0)) {
      console.log('CustomerAnalysis - Using salesMarginByAccount as fallback, hasSalesInSixMonth:', hasSalesInSixMonth);
      safeSalesMarginData.forEach(item => {
        const accountId = item.account || item.Account;
        if (!accountId) return;
        
        // Skip if already processed from sixMonthSalesData
        if (processedCustomers.find(c => c.account === accountId)) return;
        
        const voucherInfo = safeVoucherData.find(v => 
          v.Acct === accountId || v.account === accountId
        ) || null;

        const salesValue = parseFloat(item.totalSales || item.invoiceValueToday || item.invoiceValue || item.totalSalesToday || item.salesToday || item.sales || 0);
        const invoiceCount = parseInt(item.invoicesToday || item.invoices || item.invoiceCount || item.count || 0);
        const marginValue = parseFloat(item.totalMargin || item.marginToday || item.margin || item.marginValue || 0);
        const marginPercent = salesValue > 0 ? ((marginValue / salesValue) * 100) : (parseFloat(item.marginPercentToday || item.marginPercent || item.marginPercentage || 0));

        processedCustomers.push({
          account: accountId || 'N/A',
          name: item.customerName || item.name || item.Name || 'Unknown',
          branch: item.branch || item.Branch || 'Unknown',
          sales: salesValue,
          invoices: invoiceCount,
          margin: marginValue,
          marginPercent: marginPercent,
          lastOrderDate: item.lastOrderDate || item.LastOrderDate || null,
          status: 'Active',
          rebateAnalysis: null,
          currentRebateAmount: 0,
          rebateProgress: 0,
          voucherSales: voucherInfo?.Sales || 0,
          voucherTarget: voucherInfo?.Target || 0,
          voucherProgress: voucherInfo?.Target ? ((voucherInfo.Sales || 0) / voucherInfo.Target * 100) : 0
        });
      });
    }

    // FALLBACK: Process ranked customer data if still no data
    if (safeCustomerRankData.length > 0 && processedCustomers.length === 0) {
      safeCustomerRankData.forEach(customer => {
        // Find matching sales/margin data
        const salesData = safeSalesMarginData.find(item => 
          item.account === customer._id || 
          item.account === customer.account ||
          item.Account === customer._id ||
          item.Account === customer.account
        ) || {};

        // Extract sales values
        const salesValue = salesData.totalSales || 
                         customer.totalSalesToday || 
                         customer.totalSales || 
                         salesData.invoiceValueToday || 
                         salesData.invoiceValue || 
                         salesData.totalSalesToday || 
                         salesData.salesToday || 
                         salesData.sales || 
                         0;

        const invoiceCount = salesData.invoicesToday || 
                           salesData.invoices || 
                           salesData.invoiceCount || 
                           salesData.count || 
                           customer.invoiceCount || 
                           customer.invoices || 
                           0;

        const marginValue = salesData.totalMargin || 
                          salesData.marginToday || 
                          salesData.margin || 
                          salesData.marginValue || 
                          customer.marginToday || 
                          customer.margin || 
                          0;

        // Calculate margin percent
        const marginPercent = salesValue && salesValue > 0 ? 
                            ((marginValue / salesValue) * 100) :
                            (salesData.marginPercentToday || 
                             salesData.marginPercent || 
                             salesData.marginPercentage || 
                             customer.marginPercent || 
                             0);

        // Find matching voucher data
        const voucherInfo = safeVoucherData.find(v => 
          v.Acct === customer._id || 
          v.Acct === customer.account ||
          v.account === customer._id ||
          v.account === customer.account
        ) || null;

        processedCustomers.push({
          account: customer._id || customer.account || 'N/A',
          name: customer.GarageName || customer.name || customer.Name || customer.customerName || 'Unknown',
          branch: customer.branch || salesData.branch || salesData.Branch || 'Unknown',
          sales: parseFloat(salesValue) || 0,
          invoices: parseInt(invoiceCount) || 0,
          margin: parseFloat(marginValue) || 0,
          marginPercent: parseFloat(marginPercent) || 0,
          lastOrderDate: customer.lastOrderDate || customer.LastOrderDate || null,
          status: customer.status || customer.Status || 'Active',
          rebateAnalysis: customer.rebateAnalysis || null,
          currentRebateAmount: customer.rebateAnalysis?.currentRebateAmount || 0,
          rebateProgress: customer.rebateAnalysis?.nextTier ? 
            Math.min(100, Math.max(0, ((customer.rebateAnalysis.netMonthSales || 0) / (customer.rebateAnalysis.nextTier.target || 1) * 100))) : 
            (customer.rebateAnalysis?.currentTier ? 100 : 0),
          voucherSales: voucherInfo?.Sales || voucherInfo?.sales || 0,
          voucherTarget: voucherInfo?.Target || voucherInfo?.target || 0,
          voucherProgress: voucherInfo?.Target || voucherInfo?.target ? 
            Math.min(100, ((voucherInfo.Sales || voucherInfo.sales || 0) / (voucherInfo.Target || voucherInfo.target) * 100)) : 0
        });
      });
    }

    // Also process any sales margin data that wasn't in ranked customers
    if (safeSalesMarginData.length > 0) {
      safeSalesMarginData.forEach(item => {
        const accountId = item.account || item.Account;
        if (!processedCustomers.find(c => c.account === accountId)) {
          const voucherInfo = safeVoucherData.find(v => 
            v.Acct === accountId || 
            v.account === accountId
          ) || null;

          processedCustomers.push({
            account: accountId || 'N/A',
            name: item.customerName || item.name || item.Name || 'Unknown',
            branch: item.branch || item.Branch || 'Unknown',
            sales: parseFloat(item.totalSales || item.invoiceValueToday || item.invoiceValue || item.totalSalesToday || item.salesToday || item.sales || 0),
            invoices: parseInt(item.invoicesToday || item.invoices || item.invoiceCount || item.count || 0),
            margin: parseFloat(item.totalMargin || item.marginToday || item.margin || item.marginValue || 0),
            marginPercent: item.totalSales && item.totalSales > 0 ? 
              ((item.totalMargin || 0) / item.totalSales * 100) : 
              parseFloat(item.marginPercentToday || item.marginPercent || item.marginPercentage || 0),
            lastOrderDate: item.lastOrderDate || item.LastOrderDate || null,
            status: 'Active',
            rebateAnalysis: null,
            currentRebateAmount: 0,
            rebateProgress: 0,
            voucherSales: voucherInfo?.Sales || 0,
            voucherTarget: voucherInfo?.Target || 0,
            voucherProgress: voucherInfo?.Target ? ((voucherInfo.Sales || 0) / voucherInfo.Target * 100) : 0
          });
        }
      });
    }

    // Filter by selected branch
    let filteredCustomers = processedCustomers;
    if (selectedBranch !== 'all') {
      filteredCustomers = processedCustomers.filter(customer => 
        customer.branch === selectedBranch
      );
    }

    // Filter by customer name if specified from query parameter
    if (selectedCustomerFilter) {
      filteredCustomers = filteredCustomers.filter(customer => {
        const customerName = (customer.name || '').toString().toUpperCase();
        const filterName = selectedCustomerFilter.toUpperCase();
        return customerName === filterName || customerName.includes(filterName) || filterName.includes(customerName);
      });
    }

    // Filter by minimum sales if specified
    if (filters.minSales && filters.minSales > 0) {
      filteredCustomers = filteredCustomers.filter(customer => 
        customer.sales >= filters.minSales
      );
    }

    // Sort by sales for top customers
    const sortedCustomers = [...filteredCustomers].sort((a, b) => 
      (b.sales || 0) - (a.sales || 0)
    );

    // Top 10 customers
    const topCustomers = sortedCustomers.slice(0, 10).map((customer, index) => ({
      rank: index + 1,
      account: customer.account,
      name: customer.name,
      sales: customer.sales || 0,
      branch: customer.branch || 'Unknown',
      margin: customer.margin || 0,
      marginPercent: customer.marginPercent || 0,
      invoices: customer.invoices || 0
    }));

    // Chart data for performance comparison (top 15)
    const chartData = sortedCustomers.slice(0, 15).map(customer => ({
      name: customer.name.length > 15 ? customer.name.substring(0, 12) + '...' : customer.name,
      sales: customer.sales || 0,
      margin: customer.margin || 0,
      invoices: customer.invoices || 0
    }));

    // Branch performance data
    const branchData = [];
    if (branchSalesData && Array.isArray(branchSalesData) && branchSalesData.length > 0) {
      const branchMap = {};
      branchSalesData.forEach(branch => {
        const branchName = branch.branch || 'Unknown Branch';
        const salesValue = parseFloat(branch.invoiceValueToday || branch.salesToday || branch.salesMonth || branch.totalSales || 0);
        
        if (branchMap[branchName]) {
          branchMap[branchName] += salesValue;
        } else {
          branchMap[branchName] = salesValue;
        }
      });
      
      Object.entries(branchMap)
        .map(([name, value]) => ({ name, value }))
        .filter(b => b.value > 0)
        .sort((a, b) => b.value - a.value)
        .forEach(branch => {
          branchData.push(branch);
        });
    } else {
      // Fallback: aggregate from processedCustomers
      const branchGroups = processedCustomers.reduce((acc, customer) => {
        const branch = (customer.branch || 'Unknown').toString().trim();
        if (!acc[branch]) {
          acc[branch] = { branch, totalSales: 0 };
        }
        acc[branch].totalSales += customer.sales || 0;
        return acc;
      }, {});
      
      Object.values(branchGroups)
        .filter(b => b.totalSales > 0)
        .sort((a, b) => b.totalSales - a.totalSales)
        .forEach(b => {
          branchData.push({ name: b.branch, value: b.totalSales });
        });
    }

    // Calculate totals for KPIs - use ALL customers (not filtered) for totals
    // BUT for total sales, use today's daily sales data to get accurate cross-branch totals
    let totalSalesFromDaily = 0;
    console.log('CustomerAnalysis - todayDailySalesData:', todayDailySalesData);
    if (todayDailySalesData?.daily && Array.isArray(todayDailySalesData.daily)) {
      // Sum invoiceValue from all branches for today
      todayDailySalesData.daily.forEach(day => {
        if (day.branches && Array.isArray(day.branches)) {
          day.branches.forEach(branch => {
            totalSalesFromDaily += parseFloat(branch.invoiceValue || 0);
          });
        } else if (day.invoiceValue) {
          // If no branches array, try direct invoiceValue
          totalSalesFromDaily += parseFloat(day.invoiceValue || 0);
        }
      });
      console.log('CustomerAnalysis - totalSalesFromDaily calculated:', totalSalesFromDaily);
    } else {
      console.log('CustomerAnalysis - todayDailySalesData.daily is not available or empty');
    }

    const allCustomersTotals = processedCustomers.reduce((acc, customer) => {
      acc.totalSales += customer.sales || 0;
      acc.totalInvoices += customer.invoices || 0;
      acc.totalCustomers += 1;
      acc.totalMargin += customer.margin || 0;
      acc.activeCustomers += customer.sales > 0 ? 1 : 0;
      acc.totalRebateAmount += customer.currentRebateAmount || 0;
      acc.totalVoucherSales += customer.voucherSales || 0;
      return acc;
    }, {
      totalSales: 0,
      totalInvoices: 0,
      totalCustomers: 0,
      totalMargin: 0,
      activeCustomers: 0,
      totalRebateAmount: 0,
      totalVoucherSales: 0
    });

    // Override total sales with accurate daily sales data if available
    if (totalSalesFromDaily > 0) {
      allCustomersTotals.totalSales = totalSalesFromDaily;
    }

    const kpis = [
      {
        key: 'total-customers',
        label: 'Total Customers',
        value: allCustomersTotals.totalCustomers,
        type: 'customers',
        format: 'number'
      },
      {
        key: 'active-customers',
        label: 'Active Customers',
        value: allCustomersTotals.activeCustomers,
        type: 'advisors',
        format: 'number'
      },
      {
        key: 'total-sales',
        label: 'Total Sales',
        value: allCustomersTotals.totalSales,
        type: 'sales',
        format: 'currency'
      },
      {
        key: 'avg-sales',
        label: 'Avg Sales/Customer',
        value: allCustomersTotals.totalCustomers > 0 ? allCustomersTotals.totalSales / allCustomersTotals.totalCustomers : 0,
        type: 'sales',
        format: 'currency'
      },
      {
        key: 'total-margin',
        label: 'Total Margin',
        value: allCustomersTotals.totalMargin,
        type: 'margin',
        format: 'currency'
      },
      {
        key: 'avg-margin-percent',
        label: 'Avg Margin %',
        value: allCustomersTotals.totalSales > 0 ? (allCustomersTotals.totalMargin / allCustomersTotals.totalSales * 100) : 0,
        type: 'margin',
        format: 'percentage'
      },
      {
        key: 'total-rebate',
        label: 'Total Rebate Value',
        value: allCustomersTotals.totalRebateAmount,
        type: 'margin',
        format: 'currency'
      },
      {
        key: 'total-voucher',
        label: 'Total Voucher Sales',
        value: allCustomersTotals.totalVoucherSales,
        type: 'sales',
        format: 'currency'
      }
    ];

    // Table data
    const tableData = filteredCustomers.map(customer => ({
      account: customer.account || 'N/A',
      name: customer.name || 'N/A',
      branch: customer.branch || 'Unknown',
      sales: customer.sales || 0,
      invoices: customer.invoices || 0,
      margin: customer.margin || 0,
      marginPercent: customer.marginPercent || 0,
      lastOrderDate: customer.lastOrderDate || null,
      status: customer.status || 'Active',
      currentRebateAmount: customer.currentRebateAmount || 0,
      rebateTier: customer.rebateAnalysis?.currentTier?.name || 'None',
      rebateProgress: customer.rebateProgress || 0,
      voucherSales: customer.voucherSales || 0,
      voucherTarget: customer.voucherTarget || 0,
      voucherProgress: customer.voucherProgress || 0
    }));

    const result = { kpis, topCustomers, chartData, branchData, tableData };
    
    // Log the final result for debugging
    console.log('CustomerAnalysis - Final processed data:', {
      kpis,
      topCustomersLength: topCustomers.length,
      chartDataLength: chartData.length,
      branchDataLength: branchData.length,
      tableDataLength: tableData.length,
      totalSales: allCustomersTotals.totalSales,
      totalCustomers: allCustomersTotals.totalCustomers,
      processedCustomersCount: processedCustomers.length
    });
    
    // Always return the processed result - don't block it with preservation logic
    // Only preserve if we actually have sales data
    if (allCustomersTotals.totalSales > 0 || allCustomersTotals.totalCustomers > 0) {
      lastGoodDataRef.current = result;
    }
    
    return result;
  }, [sixMonthSalesData, customerRankData, salesMarginByAccount, voucherData, selectedBranch, selectedCustomerFilter, filters.minSales, branchSalesData, todayDailySalesData, sixMonthLoading, customerRankLoading, salesMarginLoading, branchesLoading]);

  // Check if data is loading - include all loading states
  const isLoading = sixMonthLoading || customerRankLoading || salesMarginLoading || branchesLoading || branchSalesLoading || loadingVoucherData || todayDailySalesLoading;

  // Export functions
  const handleExportPDF = () => {
    exportToPDF({
      title: 'Customer Analysis Report',
      tableData: processedData.tableData,
      columns: [
        { header: 'Account', key: 'account' },
        { header: 'Name', key: 'name' },
        { header: 'Branch', key: 'branch' },
        { header: 'Sales', key: 'sales', format: 'currency' },
        { header: 'Invoices', key: 'invoices' },
        { header: 'Margin', key: 'margin', format: 'currency' },
        { header: 'Margin %', key: 'marginPercent', format: 'percentage' }
      ],
      kpis: processedData.kpis
    });
  };

  const handleExportExcel = () => {
    exportToExcel(processedData.tableData, {
      title: 'Customer Analysis Report',
      columns: [
        { header: 'Account', key: 'account' },
        { header: 'Name', key: 'name' },
        { header: 'Branch', key: 'branch' },
        { header: 'Sales', key: 'sales', format: 'currency' },
        { header: 'Invoices', key: 'invoices' },
        { header: 'Margin', key: 'margin', format: 'currency' },
        { header: 'Margin %', key: 'marginPercent', format: 'percentage' }
      ],
      kpis: processedData.kpis
    });
  };

  const handleEmailReport = () => {
    const emailData = prepareEmailData({
      title: 'Customer Analysis Report',
      data: processedData.tableData,
      kpis: processedData.kpis
    });
    // Email functionality would go here
    console.log('Email data prepared:', emailData);
  };

  const handleClearCustomerFilter = () => {
    setSelectedCustomerFilter(null);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('customer');
    setSearchParams(newParams, { replace: true });
  };

  // Table columns
  const columns = [
    {
      key: 'account',
      label: 'Account',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer">
          {value}
        </span>
      )
    },
    {
      key: 'name',
      label: 'Customer Name',
      sortable: true,
      render: (value) => (
        <span className="text-gray-900">{value}</span>
      )
    },
    {
      key: 'branch',
      label: 'Branch',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">{value}</span>
      )
    },
    {
      key: 'sales',
      label: 'Sales',
      sortable: true,
      type: 'currency',
      render: (value) => (
        <span className="font-semibold text-gray-900">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'invoices',
      label: 'Invoices',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">{value}</span>
      )
    },
    {
      key: 'margin',
      label: 'Margin',
      sortable: true,
      type: 'currency',
      render: (value) => (
        <span className="font-semibold text-gray-900">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'marginPercent',
      label: 'Margin %',
      sortable: true,
      type: 'percentage',
      render: (value) => (
        <span className="text-gray-700">{formatPercentage(value)}</span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">{value}</span>
      )
    },
    {
      key: 'currentRebateAmount',
      label: 'Rebate Amount',
      sortable: true,
      type: 'currency',
      render: (value) => (
        <span className="text-gray-700">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'rebateTier',
      label: 'Rebate Tier',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">{value}</span>
      )
    },
    {
      key: 'rebateProgress',
      label: 'Rebate Progress',
      sortable: true,
      type: 'percentage',
      render: (value) => (
        <span className="text-gray-700">{formatPercentage(value)}</span>
      )
    },
    {
      key: 'voucherSales',
      label: 'Voucher Sales',
      sortable: true,
      type: 'currency',
      render: (value) => (
        <span className="text-gray-700">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'voucherTarget',
      label: 'Voucher Target',
      sortable: true,
      type: 'currency',
      render: (value) => (
        <span className="text-gray-700">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'voucherProgress',
      label: 'Voucher Progress',
      sortable: true,
      type: 'percentage',
      render: (value) => (
        <span className="text-gray-700">{formatPercentage(value)}</span>
      )
    }
  ];

  return (
    <ReportLayout
      title="Customer Analysis Report"
      subtitle="Comprehensive customer performance analysis with sales metrics and trends"
      icon={Users}
      iconColor="purple"
      onExportPDF={handleExportPDF}
      onExportExcel={handleExportExcel}
      onEmailReport={handleEmailReport}
      loading={isLoading}
      rightActions={
        selectedCustomerFilter ? (
          <button
            onClick={handleClearCustomerFilter}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Clear Customer Filter
          </button>
        ) : null
      }
    >
      {!isLoading && (
        <>
      {selectedCustomerFilter && (
        <div className="mb-4 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-indigo-800">
              Showing data for customer: <strong>{selectedCustomerFilter}</strong>
              {processedData.topCustomers.length === 0 && (
                <span className="ml-2 text-indigo-600">(No matching customers found)</span>
              )}
            </span>
            <button
              onClick={handleClearCustomerFilter}
              className="text-sm text-indigo-600 hover:text-indigo-800 underline"
            >
              Show all customers
            </button>
          </div>
        </div>
      )}
      <div className="space-y-8">
        {/* KPI Cards */}
        <ReportKPICards kpis={processedData.kpis} loading={isLoading} />

        {/* Top 10 Customers Leaderboard */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <TrendingUp className="h-6 w-6 text-blue-500" />
              <h2 className="text-xl font-bold text-gray-900">Top 10 Customers</h2>
            </div>
            <div className="text-sm text-gray-500">
              {selectedBranch === 'all' ? 'All Branches' : selectedBranch}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 lg:grid-cols-5 gap-4">
            {processedData.topCustomers.slice(0, 10).map((customer, index) => (
              <div
                key={customer.account}
                className={`p-4 rounded-lg border-2 ${
                  index === 0 ? 'border-amber-400 bg-amber-50' :
                  index === 1 ? 'border-gray-300 bg-gray-50' :
                  index === 2 ? 'border-orange-300 bg-orange-50' :
                  'border-gray-200 bg-white'
                }`}
              >
                <div className="text-center">
                  <div className={`text-2xl font-bold ${
                    index === 0 ? 'text-amber-600' :
                    index === 1 ? 'text-gray-600' :
                    index === 2 ? 'text-orange-600' :
                    'text-gray-500'
                  }`}>
                    #{customer.rank}
                  </div>
                  <div className="text-sm font-semibold text-gray-900 mt-1 truncate" title={customer.name}>
                    {customer.name.length > 15 ? customer.name.substring(0, 15) + '...' : customer.name}
                  </div>
                  <div className="text-xs text-gray-600 mb-2">
                    {customer.account}
                  </div>
                  <div className="text-lg font-bold text-green-600">
                    {formatCurrency(customer.sales)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {customer.invoices} invoices
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales Performance Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Top Customers Sales</h3>
            </div>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={processedData.chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis tickFormatter={(value) => `£${(value/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="sales" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Branch Distribution Pie Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <PieChart className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Sales by Branch</h3>
            </div>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <RechartsPieChart>
                  <Pie
                    data={processedData.branchData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                  >
                    {processedData.branchData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'][index % 5]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Tabbed Customer Tables */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900">Customer Analysis</h3>
            </div>
            <div className="flex gap-2">
              <button
                className={clsx(
                  'px-4 py-2 rounded-lg font-medium transition-colors',
                  activeTab === 'spends' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
                onClick={() => setActiveTab('spends')}
              >
                Customer Spends
              </button>
              <button
                className={clsx(
                  'px-4 py-2 rounded-lg font-medium transition-colors',
                  activeTab === 'rebates' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
                onClick={() => setActiveTab('rebates')}
              >
                Rebate Tracker
              </button>
              <button
                className={clsx(
                  'px-4 py-2 rounded-lg font-medium transition-colors',
                  activeTab === 'vouchers' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
                onClick={() => setActiveTab('vouchers')}
              >
                Voucher Tracker
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'spends' && (
            <DataTable
              data={processedData.tableData.map(customer => ({
                account: customer.account,
                name: customer.name,
                branch: customer.branch,
                sales: customer.sales,
                invoices: customer.invoices,
                margin: customer.margin,
                marginPercent: customer.marginPercent,
                status: customer.status
              }))}
              columns={columns.filter(col => 
                ['account', 'name', 'branch', 'sales', 'invoices', 'margin', 'marginPercent', 'status'].includes(col.key)
              )}
              loading={isLoading}
              searchable={true}
              sortable={true}
              pagination={true}
              pageSize={20}
              emptyMessage="No customer data available"
              searchPlaceholder="Search customers..."
            />
          )}

          {activeTab === 'rebates' && (
            <DataTable
              data={processedData.tableData
                .filter(customer => customer.currentRebateAmount > 0 || customer.rebateProgress > 0)
                .map(customer => ({
                  account: customer.account,
                  name: customer.name,
                  branch: customer.branch,
                  currentRebateAmount: customer.currentRebateAmount,
                  rebateTier: customer.rebateTier,
                  rebateProgress: customer.rebateProgress,
                  sales: customer.sales
                }))}
              columns={columns.filter(col => 
                ['account', 'name', 'branch', 'currentRebateAmount', 'rebateTier', 'rebateProgress', 'sales'].includes(col.key)
              )}
              loading={isLoading}
              searchable={true}
              sortable={true}
              pagination={true}
              pageSize={20}
              emptyMessage="No rebate data available"
              searchPlaceholder="Search customers..."
            />
          )}

          {activeTab === 'vouchers' && (
            <DataTable
              data={processedData.tableData
                .filter(customer => customer.voucherTarget > 0)
                .map(customer => ({
                  account: customer.account,
                  name: customer.name,
                  branch: customer.branch,
                  voucherSales: customer.voucherSales,
                  voucherTarget: customer.voucherTarget,
                  voucherProgress: customer.voucherProgress,
                  sales: customer.sales
                }))}
              columns={columns.filter(col => 
                ['account', 'name', 'branch', 'voucherSales', 'voucherTarget', 'voucherProgress', 'sales'].includes(col.key)
              )}
              loading={isLoading}
              searchable={true}
              sortable={true}
              pagination={true}
              pageSize={20}
              emptyMessage="No voucher data available"
              searchPlaceholder="Search customers..."
            />
          )}
        </div>
      </div>
        </>
      )}
    </ReportLayout>
  );
};

export default CustomerAnalysis;
